import React, { useLayoutEffect, useRef } from 'react'

import DevLog from '../../../modules/devlog/Module'

import { ISubscriptionUnsubscribe } from '@aytm/lib/common/subscription'
import { IViewport } from '../../../modules/viewport/types'
import { ISessionAPI } from '../../../session/types'

export interface IDetectFrameFullscreenHeight {
  (): number
}

export default function useDetectFrameFullscreenHeight(
  onFrameFullscreenHeight: (height: number) => void,
  innerElementRef: React.MutableRefObject<HTMLDivElement>,
  session: ISessionAPI,
  viewport: IViewport,
  isLoaded: boolean
): void {
  const onFrameFullscreenHeightRef = useRef<typeof onFrameFullscreenHeight>(null)
  onFrameFullscreenHeightRef.current = onFrameFullscreenHeight

  const detectFrameFullscreenHeightRef = useRef<IDetectFrameFullscreenHeight>(null)
  if (null == detectFrameFullscreenHeightRef.current) {
    detectFrameFullscreenHeightRef.current = () => {
      if (
        null != innerElementRef.current &&
        null != session.element &&
        session.document?.contains(innerElementRef.current) &&
        session.document?.contains(session.element)
      ) {
        // hide all children of frameElement
        const frameElementChildren = innerElementRef.current.children
        // eslint-disable-next-line unicorn/no-new-array
        const frameElementChildrenDisplayBackup = new Array<CSSStyleDeclaration['display']>(frameElementChildren.length)
        for (let i = 0; i < frameElementChildren.length; i++) {
          const child = frameElementChildren[i]
          if (Node.ELEMENT_NODE === child?.nodeType) {
            const element = child as HTMLElement
            frameElementChildrenDisplayBackup[i] = element.style.display
            element.style.display = 'none'
          }
        }

        // calculate hights
        const sessionElementRect = session.element.getBoundingClientRect()
        const sessionElementHeight = sessionElementRect.height || sessionElementRect.bottom - sessionElementRect.top
        const sessionElementComputedStyle = window.getComputedStyle(session.element)
        const sessionElementMarginTop = parseFloat(sessionElementComputedStyle.marginTop)
        const sessionElementMarginBottom = parseFloat(sessionElementComputedStyle.marginBottom)
        const frameElementRect = innerElementRef.current.getBoundingClientRect()
        const frameElementHeight = frameElementRect.height || frameElementRect.bottom - frameElementRect.top
        const viewportHeight = viewport.height

        // restore visibility of frameElement children
        for (let i = 0; i < frameElementChildren.length; i++) {
          const child = frameElementChildren[i]
          if (Node.ELEMENT_NODE === child?.nodeType) {
            const element = child as HTMLElement
            element.style.display = frameElementChildrenDisplayBackup[i]
          }
        }

        const frameFullscreenHeight =
          viewportHeight -
          (Math.min(viewportHeight, sessionElementHeight) - frameElementHeight) -
          sessionElementMarginTop -
          sessionElementMarginBottom

        DevLog._log('SW(QuestionFrame): detected frameFullscreenHeight', frameFullscreenHeight)

        return frameFullscreenHeight
      }
      return null
    }
  }

  useLayoutEffect(() => {
    let unsubscribeViewport: ISubscriptionUnsubscribe = null
    if (onFrameFullscreenHeightRef.current && innerElementRef.current) {
      const frameFullscreenHeight = detectFrameFullscreenHeightRef.current()
      if (null !== frameFullscreenHeight && onFrameFullscreenHeightRef.current) {
        onFrameFullscreenHeightRef.current(frameFullscreenHeight)
        unsubscribeViewport = viewport.onUpdate(() => {
          const frameFullscreenHeight = detectFrameFullscreenHeightRef.current()
          if (null !== frameFullscreenHeight && onFrameFullscreenHeightRef.current) {
            onFrameFullscreenHeightRef.current(frameFullscreenHeight)
          }
        })
      }
    }
    return () => {
      if (unsubscribeViewport) {
        unsubscribeViewport()
      }
    }
  }, [isLoaded, innerElementRef, viewport])
}
